/**
 * Admin attribute-fields (PIM Field Library) API layer — PIM Wave P1 Task 2.
 * Typed calls to the SHIPPED PIM Wave P0 `/admin/catalog/*` router
 * (`apps/api/garden/features/catalog_schema/router.py`), gated server-side
 * on `marketplace.attribute.manage`. This task adds NO backend routes.
 *
 * Mirrors the Pydantic schemas in `catalog_schema/schemas.py`:
 * `FieldGroup{Create,Update,Response,ListResponse}`,
 * `Field{Create,Update,Response,ListResponse}`,
 * `Option{Create,Update,Response,ListResponse}`. All three admin DTOs use
 * FLAT trilingual fields (`name_ru`/`name_ky`/`name_en`,
 * `hint_ru`/`hint_ky`/`hint_en`, `example_ru`/`example_ky`/`example_en`,
 * `label_ru`/`label_ky`/`label_en`) — NOT the nested `TrilingualText` used
 * by the seller-facing form-schema contract.
 *
 * `matching_weight` is a `Decimal` on the server, serialised as a JSON
 * **string** on the wire (mirrors `price_kgs` in `products/api.ts`) — the UI
 * keeps it a string to the API boundary.
 *
 * Category-field bindings (`/admin/catalog/categories/{id}/bindings*`) are
 * OUT OF SCOPE here — Task 8 (`category-bindings/`) owns that surface.
 */
import { apiClient, apiFetch } from "@/lib/api-client";

/** `FIELD_TYPE_VALUES` (`catalog_schema/models.py`) — the 7 field types. */
export const FIELD_TYPE_VALUES = [
  "text",
  "number",
  "boolean",
  "select",
  "size",
  "date",
  "image",
] as const;
export type FieldTypeValue = (typeof FIELD_TYPE_VALUES)[number];

/** `UNIT_KIND_VALUES` (`catalog_schema/models.py`) — size-field unit kinds. */
export const UNIT_KIND_VALUES = [
  "length",
  "area",
  "volume",
  "weight",
  "count",
] as const;
export type UnitKindValue = (typeof UNIT_KIND_VALUES)[number];

// ---------------------------------------------------------------------
// Field groups — mirrors FieldGroup{Create,Update,Response,ListResponse}.
// ---------------------------------------------------------------------

/** Mirrors `FieldGroupResponse`. */
export interface AttributeFieldGroup {
  id: string;
  slug: string;
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  sort_order: number;
}

/** Mirrors `FieldGroupListResponse` — full ordered list, un-paginated. */
export interface AttributeFieldGroupListResponse {
  groups: AttributeFieldGroup[];
}

/** Body for `POST /admin/catalog/field-groups` (mirrors `FieldGroupCreate`). */
export interface AttributeFieldGroupCreate {
  slug: string;
  name_ru: string;
  name_ky?: string | null;
  name_en?: string | null;
  sort_order?: number;
}

/**
 * Body for `PATCH /admin/catalog/field-groups/{id}` (mirrors
 * `FieldGroupUpdate`, partial only). No `slug` — immutable after create.
 */
export interface AttributeFieldGroupUpdate {
  name_ru?: string;
  name_ky?: string | null;
  name_en?: string | null;
  sort_order?: number;
}

export function listAttributeFieldGroups(): Promise<AttributeFieldGroupListResponse> {
  return apiClient.get<AttributeFieldGroupListResponse>(
    "/admin/catalog/field-groups",
  );
}

export function createAttributeFieldGroup(
  body: AttributeFieldGroupCreate,
): Promise<AttributeFieldGroup> {
  return apiClient.post<AttributeFieldGroup, AttributeFieldGroupCreate>(
    "/admin/catalog/field-groups",
    body,
  );
}

export function updateAttributeFieldGroup(
  id: string,
  body: AttributeFieldGroupUpdate,
): Promise<AttributeFieldGroup> {
  // PATCH partial-update — no `apiClient.patch` shortcut (categories/products precedent).
  return apiFetch<AttributeFieldGroup, AttributeFieldGroupUpdate>({
    method: "PATCH",
    path: `/admin/catalog/field-groups/${id}`,
    body,
  });
}

export function deleteAttributeFieldGroup(id: string): Promise<void> {
  // 204 hard delete — no response body.
  return apiClient.delete<void>(`/admin/catalog/field-groups/${id}`);
}

// ---------------------------------------------------------------------
// Fields — mirrors Field{Create,Update,Response,ListResponse}.
// ---------------------------------------------------------------------

/** Mirrors `FieldResponse`. */
export interface AttributeField {
  id: string;
  slug: string;
  field_type: string;
  name_ru: string;
  name_ky: string | null;
  name_en: string | null;
  hint_ru: string | null;
  hint_ky: string | null;
  hint_en: string | null;
  example_ru: string | null;
  example_ky: string | null;
  example_en: string | null;
  group_id: string | null;
  unit_kind: string | null;
  canonical_unit: string | null;
  /** `Decimal` on the server, serialised as a JSON string on the wire. */
  matching_weight: string;
  is_archived: boolean;
}

/** Mirrors `FieldListResponse` — keyset-paginated. */
export interface AttributeFieldListResponse {
  fields: AttributeField[];
  next_cursor: string | null;
}

/**
 * Body for `POST /admin/catalog/fields` (mirrors `FieldCreate`). `slug` and
 * `field_type` are set once at create and are immutable thereafter (not on
 * `AttributeFieldUpdate` at all). Size-field coherence (`unit_kind` +
 * `canonical_unit` both required when `field_type==="size"`, both forbidden
 * otherwise) is enforced server-side (422) — not re-validated here.
 */
export interface AttributeFieldCreate {
  slug: string;
  field_type: string;
  name_ru: string;
  name_ky?: string | null;
  name_en?: string | null;
  hint_ru?: string | null;
  hint_ky?: string | null;
  hint_en?: string | null;
  example_ru?: string | null;
  example_ky?: string | null;
  example_en?: string | null;
  group_id?: string | null;
  unit_kind?: string | null;
  canonical_unit?: string | null;
  matching_weight?: string;
}

/**
 * Body for `PATCH /admin/catalog/fields/{id}` (mirrors `FieldUpdate`,
 * partial only). `slug`/`field_type` intentionally absent — the backend
 * DTO is `extra="forbid"` and rejects either key with a 422.
 */
export interface AttributeFieldUpdate {
  name_ru?: string;
  name_ky?: string | null;
  name_en?: string | null;
  hint_ru?: string | null;
  hint_ky?: string | null;
  hint_en?: string | null;
  example_ru?: string | null;
  example_ky?: string | null;
  example_en?: string | null;
  group_id?: string | null;
  unit_kind?: string | null;
  canonical_unit?: string | null;
  matching_weight?: string;
}

/** Query params for `GET /admin/catalog/fields` — exact router param names. */
export interface ListAttributeFieldsParams {
  cursor?: string | null;
  limit?: number;
  field_type?: string | null;
  group_id?: string | null;
  is_archived?: boolean | null;
  q?: string | null;
}

export function listAttributeFields(
  params: ListAttributeFieldsParams,
): Promise<AttributeFieldListResponse> {
  const query = new URLSearchParams();
  if (params.limit) query.set("limit", String(params.limit));
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.field_type) query.set("field_type", params.field_type);
  if (params.group_id) query.set("group_id", params.group_id);
  if (params.is_archived !== undefined && params.is_archived !== null) {
    query.set("is_archived", String(params.is_archived));
  }
  if (params.q) query.set("q", params.q);
  const qs = query.toString();
  return apiClient.get<AttributeFieldListResponse>(
    `/admin/catalog/fields${qs ? `?${qs}` : ""}`,
  );
}

export function getAttributeField(id: string): Promise<AttributeField> {
  return apiClient.get<AttributeField>(`/admin/catalog/fields/${id}`);
}

/** Mirrors the backend's `MAX_LIST_LIMIT` (`catalog_schema/repository.py:67`)
 * — draining should page at the server's max, not its default (25). */
const DRAIN_LIMIT = 100;

/**
 * Drains `GET /admin/catalog/fields` (keyset-paginated) at the max page size
 * until `next_cursor` is null, returning the complete field catalogue —
 * INCLUDING archived (`is_archived: null` sends no server-side filter).
 * Mirrors `categories/api.ts`'s `listAllAdminCategories` drain pattern
 * exactly. Additive for PIM Wave P1 Task 8 (`category-bindings/`): the
 * binding editor's row-name lookups and add-field picker both need the full
 * field set, not a paginated slice — this is the SAME drain shape the
 * category tree already needed for categories (recon C4), just applied to
 * fields.
 */
export async function listAllAttributeFields(): Promise<AttributeField[]> {
  const all: AttributeField[] = [];
  let cursor: string | null = null;
  for (;;) {
    const page: AttributeFieldListResponse = await listAttributeFields({
      cursor,
      limit: DRAIN_LIMIT,
      is_archived: null,
    });
    all.push(...page.fields);
    if (!page.next_cursor) break;
    cursor = page.next_cursor;
  }
  return all;
}

export function createAttributeField(
  body: AttributeFieldCreate,
): Promise<AttributeField> {
  return apiClient.post<AttributeField, AttributeFieldCreate>(
    "/admin/catalog/fields",
    body,
  );
}

export function updateAttributeField(
  id: string,
  body: AttributeFieldUpdate,
): Promise<AttributeField> {
  return apiFetch<AttributeField, AttributeFieldUpdate>({
    method: "PATCH",
    path: `/admin/catalog/fields/${id}`,
    body,
  });
}

export function archiveAttributeField(id: string): Promise<AttributeField> {
  // Dedicated POST route (NOT a PATCH) — no request body.
  return apiClient.post<AttributeField, Record<string, never>>(
    `/admin/catalog/fields/${id}/archive`,
    {},
  );
}

export function unarchiveAttributeField(id: string): Promise<AttributeField> {
  return apiClient.post<AttributeField, Record<string, never>>(
    `/admin/catalog/fields/${id}/unarchive`,
    {},
  );
}

export function deleteAttributeField(id: string): Promise<AttributeField> {
  // 200 with a body (soft delete) — NOT 204, unlike field-groups.
  return apiClient.delete<AttributeField>(`/admin/catalog/fields/${id}`);
}

// ---------------------------------------------------------------------
// Field options (nested under a field) — mirrors
// Option{Create,Update,Response,ListResponse}. No DELETE route exists for
// options (archive/unarchive only); no dedicated batch reorder route
// (reorder = per-option PATCH sort_order).
// ---------------------------------------------------------------------

/** Mirrors `OptionResponse`. */
export interface AttributeFieldOption {
  id: string;
  field_id: string;
  value_slug: string;
  label_ru: string;
  label_ky: string | null;
  label_en: string | null;
  sort_order: number;
  is_archived: boolean;
}

/** Mirrors `OptionListResponse` — full list (per field). */
export interface AttributeFieldOptionListResponse {
  options: AttributeFieldOption[];
}

/**
 * Body for `POST /admin/catalog/fields/{field_id}/options` (mirrors
 * `OptionCreate`).
 */
export interface AttributeFieldOptionCreate {
  value_slug: string;
  label_ru: string;
  label_ky?: string | null;
  label_en?: string | null;
  sort_order?: number;
}

/**
 * Body for `PATCH .../options/{option_id}` (mirrors `OptionUpdate`, partial
 * only). No `value_slug` — immutable after create.
 */
export interface AttributeFieldOptionUpdate {
  label_ru?: string;
  label_ky?: string | null;
  label_en?: string | null;
  sort_order?: number;
}

export function listAttributeFieldOptions(
  fieldId: string,
): Promise<AttributeFieldOptionListResponse> {
  return apiClient.get<AttributeFieldOptionListResponse>(
    `/admin/catalog/fields/${fieldId}/options`,
  );
}

export function createAttributeFieldOption(
  fieldId: string,
  body: AttributeFieldOptionCreate,
): Promise<AttributeFieldOption> {
  return apiClient.post<AttributeFieldOption, AttributeFieldOptionCreate>(
    `/admin/catalog/fields/${fieldId}/options`,
    body,
  );
}

export function updateAttributeFieldOption(
  fieldId: string,
  optionId: string,
  body: AttributeFieldOptionUpdate,
): Promise<AttributeFieldOption> {
  return apiFetch<AttributeFieldOption, AttributeFieldOptionUpdate>({
    method: "PATCH",
    path: `/admin/catalog/fields/${fieldId}/options/${optionId}`,
    body,
  });
}

export function archiveAttributeFieldOption(
  fieldId: string,
  optionId: string,
): Promise<AttributeFieldOption> {
  return apiClient.post<AttributeFieldOption, Record<string, never>>(
    `/admin/catalog/fields/${fieldId}/options/${optionId}/archive`,
    {},
  );
}

export function unarchiveAttributeFieldOption(
  fieldId: string,
  optionId: string,
): Promise<AttributeFieldOption> {
  return apiClient.post<AttributeFieldOption, Record<string, never>>(
    `/admin/catalog/fields/${fieldId}/options/${optionId}/unarchive`,
    {},
  );
}

// ---------------------------------------------------------------------
// Query-key factories (consumed by hooks.ts).
// ---------------------------------------------------------------------

/** Filter shape keyed into the fields-list query key. */
export interface AttributeFieldFilters {
  fieldType: string | null;
  groupId: string | null;
  isArchived: boolean | null;
  q: string | null;
}

export const attributeFieldKeys = {
  /** `["admin","attribute-fields","list",filters]` — the list family. */
  list: (filters: AttributeFieldFilters) =>
    ["admin", "attribute-fields", "list", filters] as const,
  /** `["admin","attribute-fields","detail",id]`. */
  detail: (id: string) => ["admin", "attribute-fields", "detail", id] as const,
  /** `["admin","attribute-fields",fieldId,"options"]` — keyed under the field id. */
  options: (fieldId: string) =>
    ["admin", "attribute-fields", fieldId, "options"] as const,
  /** The full-drain query (`useAllAttributeFields`, PIM Wave P1 Task 8
   * additive) — mirrors `categoryKeys.all()`. NOTE (flagged, see Task 8
   * report): unlike `categoryKeys.all()` (covered by
   * `useInvalidateCategories`'s `["admin","categories"]` prefix-invalidate),
   * this key sits OUTSIDE the `["admin","attribute-fields","list"]` prefix
   * that `useInvalidateAfterFieldWrite` invalidates, so a field
   * create/patch/archive from `FieldLibraryPage` does not proactively
   * invalidate this drain — it still self-heals via `staleTime` on the next
   * mount. Left as-is: broadening the shared field-write invalidate helper
   * is outside this task's additive-only scope. */
  all: () => ["admin", "attribute-fields", "all"] as const,
} as const;

export const attributeFieldGroupKeys = {
  /** `["admin","attribute-field-groups"]`. */
  list: () => ["admin", "attribute-field-groups"] as const,
} as const;
